import React, { useState, useEffect, useRef } from 'react';

const WeatherDashboard = () => {
  const [city, setCity] = useState('London');
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputCity, setInputCity] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const debounceRef = useRef(null);

  // A. Main fetch effect with proper dependencies
  useEffect(() => {
    if (!city) return; // Conditional side effect
    
    const controller = new AbortController();
    
    const fetchWeather = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=897728442acf81f098fe6d80778835c2&units=metric`,
          { signal: controller.signal }
        );
        
        if (!response.ok) {
          throw new Error(response.status === 404 ? 'City not found' : 'Unable to fetch data');
        }
        
        const data = await response.json();
        setWeather(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    
    // B. Cleanup function
    return () => controller.abort();
  }, [city]); // Only triggers when city changes

  // C. Auto-refresh effect (separate concern)
  useEffect(() => {
    if (!autoRefresh || !city) return;
    
    const interval = setInterval(() => {
      setCity(prev => prev + ''); // Trigger re-fetch without changing city
    }, 60000); // 60 seconds
    
    // Cleanup timer
    return () => clearInterval(interval);
  }, [autoRefresh, city]);

  // C. Keyboard event listener effect
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && inputCity.trim()) {
        handleGetWeather();
      }
    };
    
    document.addEventListener('keypress', handleKeyPress);
    
    // Cleanup event listener
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [inputCity]);

  // C. Debounced search function
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputCity(value);
    
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounced API call (500ms delay)
    if (value.trim().length > 2) {
      debounceRef.current = setTimeout(() => {
        setCity(value.trim());
      }, 500);
    }
  };

  const handleGetWeather = () => {
    if (inputCity.trim()) {
      setCity(inputCity.trim());
      setInputCity('');
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="weather-dashboard">
      <h1>Weather Dashboard</h1>
      
      <div className="controls">
        <label>
          <input 
            type="checkbox" 
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (60s)
        </label>
      </div>
      
      <div className="search-section">
        <input 
          type="text" 
          value={inputCity}
          onChange={handleInputChange}
          placeholder="Enter city name (auto-search after 3 chars)" 
          className="city-input"
        />
        <button onClick={handleGetWeather} className="get-weather-btn">
          Get Weather
        </button>
      </div>

      <div className="weather-info">
        {loading && <div className="loading">Loading weather data...</div>}
        
        {error && <div className="error">{error}</div>}
        
        {weather && !loading && !error && (
          <>
            <h2>{weather.name}, {weather.sys.country}</h2>
            <div className="temperature">{Math.round(weather.main.temp)}°C</div>
            <div className="condition">{weather.weather[0].description}</div>
            <div className="details">
              <div className="detail-item">
                <span>Humidity:</span>
                <span>{weather.main.humidity}%</span>
              </div>
              <div className="detail-item">
                <span>Wind Speed:</span>
                <span>{weather.wind.speed} m/s</span>
              </div>
            </div>
            {autoRefresh && <div className="refresh-indicator">Auto-refreshing...</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default WeatherDashboard;